/* eslint-disable @typescript-eslint/no-explicit-any */
import { AngularLayout, FlexLayout } from '@rhtml/modifiers';
import { AbstractInput, FormArray, FormGroup, InputErrorMessage } from '@rxdi/forms';
import { Component, css, html, LitElement, nothing, property, state, styleMap } from '@rxdi/lit-html';

import { DropdownComponent } from '../dropdown/dropdown.component';
import { BashEditorComponent, CodeEditorComponent, JsonInputComponent } from '../editors';
import { applyTransform, BuiltInTransform } from './form-transforms';

export interface FormFieldValidator {
  name: string;
  args?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function InputErrorTemplate(input: AbstractInput | HTMLInputElement) {
  const touched = (input as any).touched;
  const validity = (input as any).validity;
  const valid = validity ? validity.valid : (input as any).valid;
  const validationMessage = (input as any).validationMessage;

  if (input && touched && !valid) {
    const message = validationMessage;
    // const key = Object.keys(InputValidityState).filter(o => input.validity[o])[0];
    // message = translate(key, 'bg_BG');
    if ('classList' in input) {
      input.classList.add('is-invalid');
    }
    return html` <span class="validation-error" part="error-message">${message}</span> `;
  }
  if (input && 'classList' in input) {
    input.classList?.remove('is-invalid');
  }
  return '';
}

function MinLengthValidator(length: number) {
  return function (element: AbstractInput | HTMLInputElement): InputErrorMessage | void {
    if (element.value && element.value.length < length) {
      return { key: 'minlength', message: `Minimum length is ${length}` };
    }
  };
}

function MaxLengthValidator(length: number) {
  return function (element: AbstractInput | HTMLInputElement): InputErrorMessage | void {
    if (element.value && element.value.length > length) {
      return { key: 'maxlength', message: `Maximum length is ${length}` };
    }
  };
}

function MinValidator(min: number) {
  return function (element: AbstractInput | HTMLInputElement): InputErrorMessage | void {
    if (element.value && Number(element.value) < min) {
      return { key: 'min', message: `Minimum value is ${min}` };
    }
  };
}

function MaxValidator(max: number) {
  return function (element: AbstractInput | HTMLInputElement): InputErrorMessage | void {
    if (element.value && Number(element.value) > max) {
      return { key: 'max', message: `Maximum value is ${max}` };
    }
  };
}

function LteFieldValidator(fieldPath: string) {
  return function (this: DynamicFormComponent, element: AbstractInput | HTMLInputElement): InputErrorMessage | void {
    const targetControl = this.form.get(fieldPath);
    if (!targetControl) return;
    if (element.value && targetControl.value && Number(element.value) > Number(targetControl.value)) {
      return {
        key: 'lteField',
        message: `Value must be less than or equal to ${targetControl.value}`,
      };
    }
  };
}

function GteFieldValidator(fieldPath: string) {
  return function (this: DynamicFormComponent, element: AbstractInput | HTMLInputElement): InputErrorMessage | void {
    const targetControl = this.form.get(fieldPath);
    if (!targetControl) return;
    if (element.value && targetControl.value && Number(element.value) < Number(targetControl.value)) {
      return {
        key: 'gteField',
        message: `Value must be greater than or equal to ${targetControl.value}`,
      };
    }
  };
}

export interface FormSelectOption {
  label: string;
  value: string | number;
}
export type FormInputTypeStandard =
  | 'text'
  | 'number'
  | 'password'
  | 'email'
  | 'select'
  | 'textarea'
  | 'checkbox'
  | 'radio'
  | any;
/**
 * Declarative field binding: when `sourceField` changes, apply `transform`
 * to its value and patch the field that owns this `bindWith` config.
 *
 * @example
 * // Auto-generate a slug from the name field:
 * bindWith: { sourceField: 'name', transform: 'slugify', transformArgs: { locale: 'bg' } }
 */
export interface FormFieldBinding {
  /** The field whose value drives this field's value. */
  sourceField: string;
  /** Named transform to apply. Built-in: 'slugify' | 'uppercase' | 'lowercase' | 'prefix' | 'regexReplace' */
  transform: BuiltInTransform | string;
  /** Optional arguments for the chosen transform. */
  transformArgs?: Record<string, string>;
  /**
   * When true (default), the target field is automatically disabled so the
   * user cannot override the derived value.
   */
  disableTarget?: boolean;
}

export interface FormField {
  name: string;
  label: string;
  type: 'array' | 'json' | 'bash' | 'code' | 'object' | 'dropdown' | 'boolean' | FormInputTypeStandard;
  minlength?: number;
  maxlength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  placeholder?: string;
  validators?: FormFieldValidator[];
  value?: string | unknown;
  options?: FormSelectOption[];
  autocomplete?: any;
  patternError?: string;
  multiple?: boolean;
  children?: FormField[];
  description?: string;
  columns?: number;
  enabledWhen?: { field: string; value: string };
  visibleWhen?: { field: string; value: string };
  disabledWhen?: { field: string; value: string };
  disabled?: boolean;
  autoDefault?: boolean;
  layout?: 'horizontal' | 'vertical';
  /** Reactive binding: derive this field's value from another field via a named transform. */
  bindWith?: FormFieldBinding;
}

export interface FormDefinition {
  name: string;
  fields: FormField[];
}

function RequiredValidator(element: AbstractInput | HTMLInputElement): InputErrorMessage | void {
  if (!element.value) {
    return { key: 'required', message: 'Field is required' };
  }
}

function RegexValidator(pattern: string, patternError?: string) {
  return function (element: AbstractInput | HTMLInputElement): InputErrorMessage | void {
    if (element.value && !new RegExp(pattern).test(element.value)) {
      return { key: 'pattern', message: patternError || 'Invalid format' };
    }
  };
}

/**
 * @customElement dynamic-form
 */
@Component({
  selector: 'dynamic-form',
  styles: [
    css`
      :host {
        display: block;
      }
      input::placeholder {
        color: var(--text-muted);
        opacity: 0.5;
      }
      .field-container {
        margin-bottom: 15px;
      }
      .is-invalid {
        box-shadow: 0 0 1px 1px var(--danger);
      }
      .validation-error {
        font-size: 0.75rem;
        color: var(--danger);
        margin-top: 4px;
        margin-left: 2px;
      }

      /* Checkbox & Radio Common Styles */
      .checkbox-group,
      .radio-group {
        display: flex;
        align-items: center;
        min-height: 32px;
        gap: 8px;
      }

      .checkbox-container,
      .radio-container {
        display: flex;
        align-items: center;
        cursor: pointer;
        user-select: none;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid var(--border-default);
        border-radius: 8px;
        padding: 0 16px;
        height: 34px;
        transition: all 0.2s ease;
      }

      .checkbox-container:hover,
      .radio-container:hover {
        border-color: var(--primary);
        background: rgba(255, 255, 255, 0.08);
      }

      .checkbox-container label,
      .radio-container label {
        display: flex;
        align-items: center;
        cursor: pointer;
        color: var(--text-primary);
        font-size: 0.9rem;
      }

      .checkbox-container input[type='checkbox'],
      .radio-container input[type='radio'] {
        appearance: none;
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        border: 1px solid var(--sf-border-focus);
        /* border-radius: 4px; Checkbox gets 4px, Radio gets 50% */
        background: rgba(255, 255, 255, 0.05);
        margin-right: 10px;
        cursor: pointer;
        position: relative;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 0;
        padding: 0px;
        margin: 0px;
        margin-right: 10px;
      }

      /* Checkbox Specific */
      .checkbox-container input[type='checkbox'] {
        border-radius: 4px;
      }

      /* Radio Specific */
      .radio-container input[type='radio'] {
        border-radius: 50%;
      }

      /* Hover States */
      .checkbox-container input[type='checkbox']:hover,
      .radio-container input[type='radio']:hover {
        border-color: var(--primary);
        background: rgba(255, 255, 255, 0.1);
      }

      /* Checked States */
      .checkbox-container input[type='checkbox']:checked,
      .radio-container input[type='radio']:checked {
        background: var(--sf-navy-light);
        border-color: var(--sf-navy-light);
      }

      /* Checkmark (Checkbox) */
      .checkbox-container input[type='checkbox']:checked::after {
        content: '';
        width: 9px;
        height: 4px;
        border-left: 2px solid white;
        border-bottom: 2px solid white;
        transform: rotate(-45deg) translate(0px, -1px);
        display: block;
      }

      /* Dot (Radio) */
      .radio-container input[type='radio']:checked::after {
        content: '';
        width: 8px;
        height: 8px;
        background: white;
        border-radius: 50%;
        display: block;
      }

      /* Focus & Disabled */
      .checkbox-container input[type='checkbox']:focus,
      .radio-container input[type='radio']:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.5);
        border-color: var(--primary);
      }

      .checkbox-container input[type='checkbox']:disabled,
      .radio-container input[type='radio']:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .group-label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        color: var(--text-muted);
        font-size: 0.85rem;
      }

      /* Array Styles */
      .array-container {
        margin-bottom: 20px;
        margin-top: 20px;
        background: rgba(255, 255, 255, 0.02);
        border: 1px dashed var(--border-default);
        border-radius: 8px;
        padding: 15px;
      }

      .array-label {
        font-size: 0.85rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--text-muted);
        font-weight: 700;
        margin-bottom: 12px;
        border-bottom: 1px solid var(--border-default);
        padding-bottom: 8px;
        display: block;
      }

      .array-row {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        background: var(--surface-glass);
        border: 1px solid var(--border-default);
        padding: 12px;
        border-radius: 8px;
        margin-bottom: 10px;
        animation: fadeIn 0.3s ease;
      }

      .array-row.single-value {
        padding: 0 !important;
        border: none !important;
        background: transparent !important;
        margin-bottom: 5px !important;
        /* align-items: center !important; */
      }

      .array-row.single-value .array-input-container {
        margin-bottom: 0 !important;
      }

      /* Hide labels and margins for single-value array items */
      .array-row.single-value .field-container {
        margin-bottom: 0 !important;
      }

      .array-row.single-value .field-container label {
        display: none !important;
      }

      .array-row.single-value .field-container input {
        margin-bottom: 0 !important;
        height: 38px; /* Match button height */
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-5px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .array-input-container {
        flex: 1;
      }

      /* Reduce bulkiness in nested fields */
      .array-input-container .field-container:last-child {
        margin-bottom: 0;
      }

      /* Buttons */
      .remove-btn {
        background: transparent;
        border: 1px solid rgba(255, 79, 79, 0.2);
        color: rgba(255, 79, 79, 0.7);
        cursor: pointer;
        padding: 0;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        height: 38px;
        width: 38px;
        flex-shrink: 0;
      }

      .remove-btn:hover {
        background: rgba(255, 79, 79, 0.1);
        color: var(--danger);
        border-color: rgba(255, 79, 79, 0.5);
      }

      .add-btn {
        width: 100%;
        background: var(--shadow-glow);
        border: 1px dashed var(--border-subtle);
        color: var(--text-body);
        padding: 10px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 0.85rem;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        margin-top: 8px;
      }

      .add-btn:hover {
        background: var(--code-editor-status-bg);
        border-color: rgba(255, 255, 255, 0.4);
        color: var(--text-body);
      }

      .param-list {
        margin-bottom: 12px;
      }

      .description {
        margin: 0px 0px 15px;
        font-size: 0.75rem;
        color: var(--text-muted);
        line-height: 1.4;
      }

      @media (max-width: 900px) {
        .checkbox-container {
          width: 100%;
        }
      }
    `,
  ],
  components: [BashEditorComponent, CodeEditorComponent, JsonInputComponent, DropdownComponent],
  modifiers: [...AngularLayout, ...FlexLayout],
  template(this: DynamicFormComponent) {
    if (!this.form || !this.definition || !this.definition?.name) return html``;

    return html`
      <form
        part="form"
        novalidate
        name=${this.definition.name}
        @submit=${async (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          const validity = await this.form.updateValueAndValidity();
          if (!validity?.length) {
            const cleanValue = this.filterSubmissionValue(this.form, this.definition.fields);
            this.dispatchEvent(
              new CustomEvent('submit', {
                detail: cleanValue,
              })
            );
          } else {
            const inputs = Array.from(
              this.shadowRoot.querySelectorAll(
                'input, select, textarea, r-dropdown, json-input, code-editor, bash-editor'
              )
            );
            const firstInvalid = inputs.find((el: HTMLInputElement) => !el.checkValidity()) as HTMLInputElement;

            if (firstInvalid) {
              firstInvalid.focus();
              firstInvalid.reportValidity();
              firstInvalid.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            } else {
              const formEl = this.shadowRoot.querySelector('form') as HTMLFormElement;
              formEl?.reportValidity();
            }
          }
        }}
      >
        <slot name="header"></slot>
        <div class="content" part="content">
          ${this.renderFields(this.definition.fields, this.form, '', this.layout ? 'two' : 'single')}
          <slot name="content-footer"></slot>
        </div>
        <div
          part="footer"
          @click=${(e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            this.form.getFormElement()?.requestSubmit();
          }}
        >
          <slot name="submit">
            <button style="width:100%; padding: 10px; cursor: pointer;" type="submit">Submit</button>
          </slot>
        </div>
        <slot name="footer"></slot>
      </form>
    `;
  },
})
export class DynamicFormComponent extends LitElement {
  @property({ type: Object })
  options: Record<string, FormSelectOption[]> = {};

  @property({ type: Object })
  definition: FormDefinition;

  @property({ type: Object })
  model: Record<string, unknown>;

  @property({ type: Object })
  errorTemplate: (input: AbstractInput) => unknown;

  @property({ type: Boolean })
  layout = false;

  @property({ type: Object })
  context: Record<string, string> = {};

  @state()
  form: FormGroup;

  /**
   * Tracks active valueChanges subscriptions created by `_setupBindings()`.
   * Keyed by the TARGET field name so we can tear them down cleanly on form rebuild.
   */
  private _bindingSubscriptions = new Map<string, { unsubscribe(): void }>();

  willUpdate(changedProperties: Map<string, unknown>) {
    // Rebuild form if definition changes
    if (changedProperties.has('definition') && this.definition) {
      this.form = this.buildForm(this.definition);
    }

    // Patch value if model changes (and form exists)
    if (changedProperties.has('model') && this.model && this.form) {
      this.form.patchValue(this.normalizeModel(this.model));
    }

    // Patch value if options change (to trigger autoDefault)
    if (changedProperties.has('options') && this.form) {
      this.form.patchValue(this.normalizeModel(this.form.value || {}));
    }
  }

  private normalizeModel(model: any) {
    if (!model || !this.definition) return {};
    const normalized = { ...model };
    this.definition.fields.forEach((field) => {
      let val = normalized[field.name];
      // 1. Fallback to default value if null/undefined
      if ((val === null || val === undefined) && field.value !== undefined) {
        val = field.value;
      }
      const options = this.options[field.name] || field.options || [];

      // Treat null, undefined, empty array, or empty string as "empty"
      const isEmpty = val === null || val === undefined || (Array.isArray(val) && val.length === 0) || val === '';

      if (isEmpty && field.autoDefault && options?.length) {
        const firstOption = options[0];
        val = typeof firstOption === 'object' && 'value' in firstOption ? firstOption.value : firstOption;
      }
      // 2. Normalize to array if multiple
      if (field.multiple) {
        if (val && !Array.isArray(val)) {
          val = [val];
        } else if (!val) {
          val = [];
        }
      }
      normalized[field.name] = val;
    });
    return normalized;
  }

  private filterSubmissionValue(group: FormGroup, fields: FormField[]): any {
    const result: any = {};
    fields.forEach((field) => {
      // Exclude if not visible
      if (!this.isFieldVisible(field)) return;
      // TODO decide if we need some special disabled but included in the form values

      // Exclude if disabled (enabledWhen condition false)
      if (!this.checkDependency(field)) return;

      const control = group.get(field.name);
      if (!control) return;

      const value = control.value;

      if (field.type === 'object' && field.children) {
        result[field.name] = this.filterSubmissionValue(control as unknown as FormGroup, field.children);
      } else if (field.type === 'array' && field.children) {
        if (value && Array.isArray(value)) {
          const arrayControl = control as unknown as FormArray;
          result[field.name] = arrayControl.controls.map((item) =>
            this.filterSubmissionValue(item as unknown as FormGroup, field.children)
          );
        } else {
          result[field.name] = [];
        }
      } else {
        if (field.type === 'number') {
          result[field.name] = value === '' || value === null || value === undefined ? null : Number(value);
        } else if (field.type === 'boolean' || (field.type === 'checkbox' && !field.options)) {
          // For single checkboxes, read the checked property from DOM element
          // because native checkbox input.value returns 'on' instead of boolean
          const inputName = field.name;
          const checkboxInput = this.shadowRoot?.querySelector(
            `input[type="checkbox"][name="${inputName}"]`
          ) as HTMLInputElement | null;
          if (checkboxInput) {
            result[field.name] = checkboxInput.checked;
          } else {
            // Fallback to value parsing if element not found
            result[field.name] = String(value) === 'true' || (value as any) === true;
          }
        } else if (field.type === 'radio') {
          // Radio buttons with 'true'/'false' values should be converted to booleans
          if (value === 'true' || (value as unknown) === true) {
            result[field.name] = true;
          } else if (value === 'false' || (value as unknown) === false) {
            result[field.name] = false;
          } else {
            result[field.name] = value;
          }
        } else {
          result[field.name] = value;
        }
      }
    });
    return result;
  }

  private buildNestedForm(field: FormField, initialValue: Record<string, unknown>): FormGroup {
    const nestedControls: Record<string, any> = {};
    const nestedValidators = new Map<string, any[]>();

    field.children?.forEach((child) => {
      const childInitialValue = initialValue?.[child.name];

      const type = child.type.toLowerCase();
      if (type === 'object' && child.children) {
        nestedControls[child.name] = this.buildNestedForm(child, (childInitialValue as Record<string, unknown>) || {});
      } else if (type === 'array') {
        nestedControls[child.name] =
          childInitialValue !== undefined ? childInitialValue : child.value !== undefined ? child.value : null;
      } else {
        nestedControls[child.name] =
          childInitialValue !== undefined ? childInitialValue : child.value !== undefined ? child.value : null;
      }

      let validators = [];
      if (child.validators) {
        validators = [...this.mapValidators(child.validators)];
      }
      if (child.minlength) validators.push(MinLengthValidator(child.minlength));
      if (child.maxlength) validators.push(MaxLengthValidator(child.maxlength));
      if (child.min) validators.push(MinValidator(child.min));
      if (child.max) validators.push(MaxValidator(child.max));
      if (child.pattern) validators.push(RegexValidator(child.pattern, child.patternError));

      if (validators.length) {
        nestedValidators.set(child.name, validators);
      }
    });

    const group = new FormGroup(nestedControls);
    nestedValidators.forEach((vals, key) => {
      group.validators.set(key, vals);
    });
    return group;
  }

  updated(changedProperties: Map<string, unknown>) {
    if (
      this.form &&
      !this.form['initialized'] &&
      this.shadowRoot.querySelector(`form[name="${this.definition.name}"]`)
    ) {
      this.form.setParentElement(this);
      this.form.init();
      this.form['initialized'] = true;
      this.form.valueChanges.subscribe((detail) => {
        this.dispatchEvent(new CustomEvent('change', { detail }));
      });
      // Set up declarative field bindings (bindWith)
      this._setupBindings();
      // this.requestUpdate();
    }
    if (this.form && this.form['initialized']) {
      this.form.init();
    }
  }

  /**
   * Subscribes to valueChanges on source fields for every FormField that
   * declares a `bindWith` config. When the source changes the derived value
   * is patched onto the target control without any consumer code.
   */
  private _setupBindings() {
    if (!this.definition?.fields || !this.form) return;

    this.definition.fields.forEach((targetField) => {
      if (!targetField.bindWith) return;

      const { sourceField, transform, transformArgs } = targetField.bindWith;
      const sourceControl = this.form.get(sourceField);
      const targetControl = this.form.get(targetField.name);

      if (!sourceControl || !targetControl) {
        console.warn(
          `[DynamicForm] bindWith: Could not find source ("${sourceField}") or target ("${targetField.name}") control.`
        );
        return;
      }

      // Tear down any existing subscription for this target to avoid duplicates
      this._bindingSubscriptions.get(targetField.name)?.unsubscribe();

      const subscription = sourceControl.valueChanges.subscribe((newValue: any) => {
        const derived = applyTransform(transform, newValue, transformArgs);
        this.form.patchValue({ [targetField.name]: derived } as any);
      });

      this._bindingSubscriptions.set(targetField.name, subscription);
    });
  }

  /** Unsubscribes all active binding subscriptions. Called before rebuilding the form. */
  private _teardownBindings() {
    this._bindingSubscriptions.forEach((sub) => sub.unsubscribe());
    this._bindingSubscriptions.clear();
  }

  private buildForm(definition: FormDefinition): FormGroup {
    // Clean up any binding subscriptions from a previous form instance
    this._teardownBindings();
    const controls: { [key: string]: any } = {};
    const validatorsMap = new Map<string, any[]>();

    const normalizedModel = this.normalizeModel(this.model || {});

    definition.fields.forEach((field) => {
      const type = field.type.toLowerCase();
      if (type === 'array') {
        const initialValue = normalizedModel[field.name];
        controls[field.name] = new FormArray([], {
          name: field.name,
          itemFactory: (value) => {
            const itemControls: { [key: string]: any } = {};
            const group = new FormGroup(itemControls);

            if (field.children) {
              field.children.forEach((child) => {
                itemControls[child.name] = value[child.name] || child.value || '';
                if (child.validators) {
                  group.validators.set(child.name, this.mapValidators(child.validators));
                }
              });
            }
            return group;
          },
        });
        if (Array.isArray(initialValue) && initialValue.length) {
          controls[field.name].patchValue(initialValue);
        }
      } else if (type === 'object' && field.children) {
        const nestedGroup = this.buildNestedForm(field, normalizedModel[field.name] || {});
        controls[field.name] = nestedGroup;
      } else {
        const modelValue = normalizedModel[field.name];
        const val = modelValue !== undefined ? modelValue : field.value !== undefined ? field.value : null;

        // Wrap array values to prevent rxdi from treating it as [Value, Validators]
        controls[field.name] = Array.isArray(val) ? [val] : val;

        let validators = [];
        if (field.validators) {
          validators = [...this.mapValidators(field.validators)];
        }
        if (field.minlength) validators.push(MinLengthValidator(field.minlength));
        if (field.maxlength) validators.push(MaxLengthValidator(field.maxlength));
        if (field.min) validators.push(MinValidator(field.min));
        if (field.max) validators.push(MaxValidator(field.max));
        if (field.pattern) validators.push(RegexValidator(field.pattern, field.patternError));

        if (validators.length) {
          validatorsMap.set(field.name, validators);
        }
      }
    });

    const form = new FormGroup(controls);
    form.setOptions({
      name: definition.name,
      strategy: 'input',
      strict: false,
      customElements: ['json-input', 'bash-editor', 'code-editor', 'r-dropdown'],
    });
    validatorsMap.forEach((vals, key) => {
      form.validators.set(key, vals);
    });
    return form;
  }

  private mapValidators(validators: FormFieldValidator[]) {
    return validators.map((v) => {
      if (v.name === 'required') return RequiredValidator;
      if (v.name === 'regex' && v.args) return RegexValidator(v.args);
      if (v.name === 'minlength' && v.args) return MinLengthValidator(parseInt(v.args, 10));
      if (v.name === 'maxlength' && v.args) return MaxLengthValidator(parseInt(v.args, 10));
      if (v.name === 'min' && v.args) return MinValidator(parseInt(v.args, 10));
      if (v.name === 'max' && v.args) return MaxValidator(parseInt(v.args, 10));
      if (v.name === 'lteField' && v.args) return LteFieldValidator(v.args);
      if (v.name === 'gteField' && v.args) return GteFieldValidator(v.args);
      return () => {
        //
      };
    });
  }

  private renderFields(
    fields: FormField[],
    parent: FormGroup | FormArray,
    prefix = '',
    layout: 'single' | 'two' = 'single'
  ) {
    const visibleFields = fields.filter((f) => this.isFieldVisible(f));
    if (layout === 'single') {
      return visibleFields.map((field) => this.renderSingleField(field, parent, prefix));
    }

    const templates = [];
    let i = 0;

    while (i < visibleFields.length) {
      const field = visibleFields[i];
      const isBlock = this.isBlockField(field);
      // debugger;
      if (isBlock) {
        // Render full width block
        templates.push(this.renderSingleField(field, parent, prefix));
        i++;
      } else {
        // Try to pair with next field
        const nextField = visibleFields[i + 1];
        if (nextField && !this.isBlockField(nextField)) {
          // Render two columns
          templates.push(html`
            <div fxLayout="row" fxLayoutGap="20px">
              <div fxFlex="50">${this.renderSingleField(field, parent, prefix)}</div>
              <div fxFlex="50">${this.renderSingleField(nextField, parent, prefix)}</div>
            </div>
          `);
          i += 2;
        } else {
          // Render single column
          templates.push(html`
            <div fxLayout="row" fxLayoutGap="20px">
              <div fxFlex="50">${this.renderSingleField(field, parent, prefix)}</div>
              <div fxFlex="50"></div>
            </div>
          `);
          i++;
        }
      }
    }
    return templates;
  }

  private isFieldVisible(field: FormField): boolean {
    if (!field.visibleWhen) {
      return true;
    }

    const { field: fieldPath, value: expectedValue } = field.visibleWhen;

    // Support context values (e.g., "context.mode")
    if (fieldPath.startsWith('context.')) {
      const contextKey = fieldPath.replace('context.', '');
      return this.context[contextKey] === expectedValue;
    }

    const control = this.form.get(fieldPath);
    if (!control) {
      console.warn('Visibility dependency control not found:', fieldPath);
      return true;
    }

    // For checkbox fields, read the actual checked state from DOM
    // because native checkbox input.value returns 'on' instead of boolean
    const dependentField = this.definition?.fields.find((f) => f.name === fieldPath);
    if (dependentField && (dependentField.type === 'checkbox' || dependentField.type === 'boolean')) {
      const checkboxInput = this.shadowRoot?.querySelector(
        `input[type="checkbox"][name="${fieldPath}"]`
      ) as HTMLInputElement | null;
      if (checkboxInput) {
        const boolExpected = (expectedValue as unknown) === true || expectedValue === 'true';
        return checkboxInput.checked === boolExpected;
      }
    }

    return control.value === expectedValue;
  }

  private isBlockField(field: FormField) {
    const type = field.type.toLowerCase();
    return ['textarea', 'json', 'bash', 'code', 'object', 'array'].includes(type);
  }

  private checkDependency(field: FormField): boolean {
    if (!field.enabledWhen) {
      return true;
    }
    const control = this.form.get(field.enabledWhen.field);
    if (!control) {
      console.warn('Dependency control not found:', field.enabledWhen.field);
      return true;
    }
    return control.value === field.enabledWhen.value;
  }

  /**
   * Checks if a field should be disabled based on:
   * 1. Static `disabled` property
   * 2. `bindWith.disableTarget` — automatically disabled when bound to another field
   * 3. `enabledWhen` condition (field is disabled if condition is NOT met)
   * 4. `disabledWhen` condition (field is disabled if condition IS met)
   *    - Supports context values like 'context.mode'
   */
  private checkDisabled(field: FormField): boolean {
    // 1. Static disabled property
    if (field.disabled) return true;

    // 2. bindWith — disable the target field unless explicitly opted out
    if (field.bindWith && field.bindWith.disableTarget !== false) return true;

    // 3. enabledWhen - disabled if condition is NOT met
    if (field.enabledWhen) {
      const isEnabled = this.checkDependency(field);
      if (!isEnabled) return true;
    }

    // 4. disabledWhen - disabled if condition IS met
    if (field.disabledWhen) {
      const { field: fieldPath, value } = field.disabledWhen;

      // Check if it's a context field (e.g., "context.mode")
      if (fieldPath.startsWith('context.')) {
        const contextKey = fieldPath.replace('context.', '');
        return this.context[contextKey] === value;
      }

      // Otherwise check from form values
      const control = this.form.get(fieldPath);
      if (control) {
        return control.value === value;
      }
    }

    return false;
  }

  private renderSingleField(field: FormField, parent: FormGroup | FormArray, prefix = '') {
    if (field.type === 'array') {
      return this.renderArray(field, parent);
    }
    const inputName = prefix ? prefix + '.' + field.name : field.name;
    const control = parent instanceof FormGroup ? parent.get(field.name) : null;
    let currentValue: any = control?.value;
    const isDisabled = this.checkDisabled(field);

    if (field.multiple && !Array.isArray(currentValue)) {
      if (currentValue) {
        currentValue = [currentValue];
      } else {
        currentValue = [];
      }
    }

    return html`
      <div class="field-container" part="field-container">
        <label part="label">${field.label}</label>
        ${this.renderInputType(field, inputName, currentValue, parent as FormGroup, isDisabled)}
        <p ngIf=${field.description} class="description">${field.description}</p>
        <div part="error">
          ${control ? (this.errorTemplate ? this.errorTemplate(control) : InputErrorTemplate(control)) : ''}
        </div>
      </div>
    `;
  }

  private renderInputType(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    if (field.type === 'select') {
      return this.renderSelect(field, name, value, parent, disabled);
    }
    if (field.type === 'textarea') {
      return this.renderTextarea(field, name, value, parent, disabled);
    }
    if (field.type === 'json') {
      return this.renderJsonInput(field, name, value, parent, disabled);
    }
    if (field.type === 'bash') {
      return this.renderBashEditor(field, name, value, parent, disabled);
    }
    if (field.type === 'code') {
      return this.renderCodeEditor(field, name, value, parent, disabled);
    }
    if (field.type === 'dropdown') {
      return this.renderDropdown(field, name, value, parent, disabled);
    }
    if (field.type === 'object') {
      return this.renderObject(field, name, value, parent);
    }
    if (field.type === 'checkbox') {
      return this.renderCheckbox(field, name, value, parent, disabled);
    }
    if (field.type === 'radio') {
      return this.renderRadio(field, name, value, parent, disabled);
    }
    return this.renderInput(field, name, value, parent, disabled);
  }

  private renderInput(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    return html`
      <input
        part="input"
        type=${field.type}
        name=${name}
        .value=${value || ''}
        minlength=${field.minlength || nothing}
        maxlength=${field.maxlength || nothing}
        min=${field.min || nothing}
        max=${field.max || nothing}
        pattern=${field.pattern || '.*'}
        ?disabled=${disabled}
        autocomplete=${field.autocomplete || 'off'}
        ?required=${field.validators?.some((v) => v.name === 'required')}
        placeholder=${field.placeholder || ''}
        @invalid=${(e: Event) => {
          const target = e.target as HTMLInputElement;
          if (target.validity.patternMismatch && field.patternError) {
            target.setCustomValidity(field.patternError);
          }
        }}
      />
    `;
  }

  private renderTextarea(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    return html`
      <textarea
        part="textarea"
        name=${name}
        .value=${value || ''}
        minlength=${field.minlength || null}
        maxlength=${field.maxlength || null}
        autocomplete=${field.autocomplete || 'off'}
        ?required=${field.validators?.some((v) => v.name === 'required')}
        ?disabled=${disabled}
        placeholder=${field.placeholder || ''}
      ></textarea>
    `;
  }

  private renderSelect(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    return html`
      <select
        part="select"
        name=${name}
        .value=${value || ''}
        ?required=${field.validators?.some((v) => v.name === 'required')}
        ?disabled=${disabled}
      >
        <option value="" disabled ?selected=${!value}>${field.label}</option>
        ${field.options?.map(
          (opt) => html`<option value=${opt.value} ?selected=${opt.value === value}>${opt.label}</option>`
        )}
      </select>
    `;
  }

  private renderArray(field: FormField, parent: FormGroup | FormArray) {
    // ... (Existing renderArray logic with added parts)
    const arrayControl = (parent as any).get(field.name) as FormArray;
    if (!arrayControl) return html``;

    const isSingleValue = field.children?.length === 1;

    return html`
      <div class="array-container" part="array-container">
        <label class="array-label" part="array-label"> ${field.label} </label>
        <div
          style=${styleMap({
            'margin-bottom': arrayControl.controls.length ? '12px' : '0',
          })}
          class="param-list"
        >
          ${arrayControl.controls.map(
            (control, index) => html`
              <div class="array-row ${isSingleValue ? 'single-value' : ''}" part="array-row">
                <div class="array-input-container" part="array-input-container">
                  ${field.children?.map(
                    (child) => html`
                      <div
                        class="array-nested-wrapper"
                        part="array-nested-wrapper"
                        style="${isSingleValue ? '' : 'margin-bottom: 8px;'}"
                      >
                        ${this.renderNestedFields([child], `${field.name}[${index}]`, control as FormGroup)}
                      </div>
                    `
                  )}
                </div>
                <button
                  type="button"
                  class="remove-btn"
                  part="remove-button"
                  @click=${() => arrayControl.removeAt(index)}
                  title="Remove Item"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            `
          )}
        </div>
        <button type="button" class="add-btn" @click=${() => this.addArrayItem(arrayControl)} part="add-button">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="14"
            height="14"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          ${field.label}
        </button>
      </div>
    `;
  }

  private addArrayItem(array: FormArray) {
    const options = array.getOptions();
    if (options && options.itemFactory) {
      array.push(options.itemFactory({}));
    }
  }

  private renderNestedFields(fields: FormField[], prefix: string, parent: FormGroup) {
    return this.renderFields(fields, parent, prefix, 'single');
  }

  private renderJsonInput(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    return html`
      <json-input
        name=${name}
        .value=${value || ''}
        ?required=${field.validators?.some((v) => v.name === 'required')}
        ?disabled=${disabled}
        ?readonly=${false}
      ></json-input>
    `;
  }

  private renderBashEditor(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    return html`
      <bash-editor
        name=${name}
        .value=${value || ''}
        ?required=${field.validators?.some((v) => v.name === 'required')}
        ?disabled=${disabled}
        ?readonly=${false}
      ></bash-editor>
    `;
  }

  private renderObject(field: FormField, name: string, value: any, parent: FormGroup) {
    const nestedGroup = parent.get(field.name) as unknown as FormGroup;
    // We reuse array-nested-wrapper style for consistency or define a new one
    // Using simple div for now
    return html`
      <div class="nested-object-wrapper" style="margin-top: 8px;">
        ${this.renderFields(field.children || [], nestedGroup, name, field.columns === 2 ? 'two' : 'single')}
      </div>
    `;
  }

  private renderCodeEditor(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    return html` <code-editor name=${name} .value=${value || ''} ?disabled=${disabled}></code-editor> `;
  }

  private renderCheckbox(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    if (field.options?.length) {
      const layoutStyle = field.layout === 'horizontal' ? 'display: flex; flex-wrap: wrap; gap: 16px;' : '';
      return html`
        <div class="checkbox-group" style=${layoutStyle}>
          ${field.options.map((opt) => {
            const isChecked = field.multiple ? Array.isArray(value) && value.includes(opt.value) : value === opt.value;

            return html`
              <div class="checkbox-container">
                <label>
                  <input
                    type="checkbox"
                    name=${name}
                    .value=${opt.value as never}
                    .checked=${isChecked}
                    ?multiple=${field.multiple}
                    ?disabled=${disabled}
                  />
                  <span style="margin-left: 8px;">${opt.label}</span>
                </label>
              </div>
            `;
          })}
        </div>
      `;
    }

    // Default Boolean Toggle
    return html`
      <div class="checkbox-container">
        <label>
          <input
            type="checkbox"
            name=${name}
            .checked=${String(value) === 'true' || value === true}
            ?disabled=${disabled}
          />
          <span style="margin-left: 8px;">${field.label}</span>
        </label>
      </div>
    `;
  }

  private renderRadio(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    const layoutStyle = field.layout === 'horizontal' ? 'display: flex; flex-wrap: wrap; gap: 16px;' : '';
    return html`
      <div class="radio-group" style=${layoutStyle}>
        ${field.options?.map(
          (opt) => html`
            <div class="radio-container">
              <label>
                <input
                  type="radio"
                  name=${name}
                  .value=${opt.value as never}
                  .checked=${value === opt.value}
                  ?disabled=${disabled}
                />
                <span style="margin-left: 8px;">${opt.label}</span>
              </label>
            </div>
          `
        )}
      </div>
    `;
  }

  private renderDropdown(field: FormField, name: string, value: any, parent: FormGroup, disabled = false) {
    return html`
      <r-dropdown
        part="dropdown"
        name=${name}
        .items=${this.options[field.name] || field.options || []}
        .value=${value || (field.multiple ? [] : '')}
        placeholder=${field.placeholder || 'Select...'}
        ?multiple=${field.multiple}
        ?disabled=${disabled}
        @change=${(e: CustomEvent) => parent.setValue(field.name, e.detail.value)}
      ></r-dropdown>
    `;
  }
}
